/**
 * Progress Bar für Shopify
 * Zeigt eine Progress Bar im Cart an und als temporäre Notification
 */

class ShopifyProgressBar {
  constructor() {
    // Initialisierung der grundlegenden Eigenschaften
    this.notificationTimer = null;
    this.notificationDuration = 30000; // 30 Sekunden
    this.cartTotal = 0;
    
    // Initial Progress Bars finden
    this.progressBars = document.querySelectorAll('.progress-bar-container');
    console.log("Initial gefundene Progress Bars:", this.progressBars.length);
    
    // Wenn das DOM noch nicht fertig geladen ist, warten wir
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initialize();
      });
    } else {
      this.initialize();
    }
  }
  
  initialize() {
    // Event-Listener für Cart-Updates
    this.setupCartEventListeners();
    
    // Warenkorb-Daten laden
    this.getCartData();
    
    // MutationObserver für dynamisch geladene Cart-Elemente einrichten
    this.setupMutationObserver();
  }
  
  setupCartEventListeners() {
    // Standard Shopify cart:updated Event
    document.addEventListener('cart:updated', (event) => {
      console.log("Cart Updated Event:", event.detail);
      if (event.detail && event.detail.cart) {
        this.cartTotal = event.detail.cart.total_price;
        this.findAndUpdateProgressBars();
        this.showNotification();
      }
    });
    
    // Manche Themes verwenden andere Events - hier einige gängige
    const additionalEvents = ['theme:cart:updated', 'cart.requestComplete', 'cart:refresh'];
    additionalEvents.forEach(eventName => {
      document.addEventListener(eventName, (event) => {
        console.log(`Alternatives Cart Event (${eventName}) erkannt`);
        this.getCartData(); // Bei alternativen Events holen wir die Daten neu
      });
    });
  }
  
  setupMutationObserver() {
    // MutationObserver für DOM-Änderungen einrichten
    const observer = new MutationObserver((mutations) => {
      // Flag, ob wir neue Elemente gefunden haben
      let newNodesAdded = false;
      
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Prüfen, ob relevante Elemente hinzugefügt wurden
          for (const node of mutation.addedNodes) {
            if (node.nodeType === 1) { // Element-Node
              // Prüfen, ob es ein Cart-Element sein könnte
              const possibleCartSelectors = ['.cart', '.cart-drawer', '#cart', '#CartDrawer'];
              if (possibleCartSelectors.some(selector => node.matches && node.matches(selector))) {
                console.log("Potenzielles Cart-Element erkannt");
                newNodesAdded = true;
                break;
              }
              
              // Oder falls es Progress Bars enthält
              if (node.querySelectorAll && node.querySelectorAll('.progress-bar-container').length > 0) {
                console.log("Neue Progress Bars gefunden");
                newNodesAdded = true;
                break;
              }
            }
          }
          
          if (newNodesAdded) break;
        }
      }
      
      // Wenn neue relevante Elemente gefunden wurden, aktualisieren wir
      if (newNodesAdded) {
        setTimeout(() => {
          this.getCartData();
          this.findAndUpdateProgressBars();
        }, 200); // Kleine Verzögerung, um sicherzustellen, dass DOM vollständig ist
      }
    });
    
    // Observer konfigurieren und starten
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    console.log("MutationObserver für DOM-Änderungen eingerichtet");
  }
  
  // Suche Progress Bars und aktualisiere sie
  findAndUpdateProgressBars() {
    // Progress Bars neu suchen, falls durch AJAX nachgeladen
    this.progressBars = document.querySelectorAll('.progress-bar-container');
    console.log("Progress Bars aktualisiert, gefunden:", this.progressBars.length);
    
    if (this.progressBars.length > 0) {
      this.updateAllProgressBars();
    }
  }
  
  // Hole Warenkorb-Daten
  getCartData() {
    console.log("Lade Warenkorb-Daten...");
    
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log("Warenkorb geladen:", {
          total_price: cart.total_price,
          item_count: cart.item_count
        });
        
        this.cartTotal = cart.total_price;
        this.findAndUpdateProgressBars();
      })
      .catch(error => console.error('Error fetching cart data:', error));
  }
  
  // Update alle Progress Bars auf der Seite
  updateAllProgressBars() {
    console.log(`Aktualisiere ${this.progressBars.length} Progress Bars`);
    
    this.progressBars.forEach((progressBar, index) => {
      console.log(`Progress Bar ${index + 1} aktualisieren`);
      this.updateProgressBar(progressBar);
    });
  }
  
  // Update einer einzelnen Progress Bar
  updateProgressBar(progressBar) {
    // Datenattribute auslesen
    const threshold1 = parseInt(progressBar.dataset.threshold1 || 0);
    const threshold2 = parseInt(progressBar.dataset.threshold2 || 0);
    const threshold3 = parseInt(progressBar.dataset.threshold3 || 0);
    const steps = parseInt(progressBar.dataset.steps || 0);
    
    console.log("Progress Bar Daten:", {
      threshold1,
      threshold2,
      threshold3,
      steps,
      displayType: progressBar.dataset.displayType
    });
    
    // Wir brauchen mindestens eine gültige Stufe
    if (steps === 0 || threshold1 === 0) {
      console.log("Keine gültigen Stufen definiert, Abbruch");
      return;
    }
    
    // Finde alle aktiven Schwellenwerte (größer als 0)
    let activeThresholds = [];
    if (threshold1 > 0) activeThresholds.push(threshold1);
    if (threshold2 > 0) activeThresholds.push(threshold2);
    if (threshold3 > 0) activeThresholds.push(threshold3);
    
    // Sortiere die Schwellenwerte
    activeThresholds.sort((a, b) => a - b);
    
    // Berechne den Fortschritt in Segmenten
    const cartValue = this.cartTotal / 100; // Shopify speichert in Cents
    const cartValueCents = this.cartTotal;
    
    // Berechne den Segmentfortschritt
    let segmentPercent = 0;
    let segmentSize = 100 / activeThresholds.length; // Größe jedes Segments in Prozent
    
    console.log("Segment-Infos:", {
      segments: activeThresholds.length,
      segmentSize: segmentSize + "%",
      cartValue: cartValue.toFixed(2) + " €"
    });
    
    // Finde die aktuelle Stufe und berechne den Fortschritt innerhalb dieser Stufe
    if (cartValueCents >= activeThresholds[activeThresholds.length - 1]) {
      // Wir haben alle Stufen erreicht
      segmentPercent = 100;
    } else {
      let currentSegment = 0;
      let previousThreshold = 0;
      
      // Finde das aktuelle Segment
      for (let i = 0; i < activeThresholds.length; i++) {
        if (cartValueCents < activeThresholds[i]) {
          currentSegment = i;
          break;
        }
        previousThreshold = activeThresholds[i];
      }
      
      // Berechne den Basis-Prozentsatz basierend auf vollständigen Segmenten
      let basePercent = currentSegment * segmentSize;
      
      // Berechne den Fortschritt innerhalb des aktuellen Segments
      let currentThreshold = activeThresholds[currentSegment];
      let segmentStart = previousThreshold;
      let segmentProgress = (cartValueCents - segmentStart) / (currentThreshold - segmentStart);
      
      console.log("Segment-Berechnung:", {
        currentSegment,
        segmentStart: (segmentStart / 100).toFixed(2) + " €",
        currentThreshold: (currentThreshold / 100).toFixed(2) + " €",
        segmentProgress: (segmentProgress * 100).toFixed(2) + "%"
      });
      
      // Addiere den Fortschritt innerhalb des aktuellen Segments
      segmentPercent = basePercent + (segmentProgress * segmentSize);
    }
    
    console.log("Fortschritt berechnet:", {
      segmentPercent: segmentPercent.toFixed(2) + "%"
    });
    
    // Aktualisiere die Progress Bar
    const progressFill = progressBar.querySelector('.progress-bar-fill');
    
    if (!progressFill) {
      console.error("Progress Fill Element nicht gefunden!");
      return;
    }
    
    // Direktes Setzen des Fortschritts (als Fallback)
    progressFill.style.width = `${segmentPercent}%`;
    
    // Animiere den Fortschritt
    this.animateProgress(progressFill, segmentPercent);
    
    // Aktualisiere die Nachrichten
    this.updateProgressMessage(progressBar, cartValue, threshold1, threshold2, threshold3);
    
    // Markiere erreichte Stufen
    this.updateProgressMarkers(progressBar, cartValue, threshold1, threshold2, threshold3);
  }
  
  // Animiere den Fortschritt der Progress Bar
  animateProgress(progressFill, targetPercent) {
    if (!progressFill) return;
    
    // Aktuelle Breite der Progress Bar
    // Wir benötigen eine numerische Breite - wenn diese nicht existiert, verwenden wir 0
    let currentWidthStr = progressFill.style.width.replace('%', '');
    const currentWidth = parseFloat(currentWidthStr) || 0;
    
    console.log("Animation Start:", {
      currentWidth: currentWidth + "%",
      targetPercent: targetPercent + "%"
    });
    
    // Wenn der aktuelle Wert und Zielwert gleich sind, nichts tun
    if (Math.abs(currentWidth - targetPercent) < 0.5) {
      return;
    }
    
    // Animation mit requestAnimationFrame
    let start = null;
    const duration = 800; // Animation Duration in ms
    
    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing-Funktion für sanfte Animation
      const easeOutQuad = progress => 1 - (1 - progress) * (1 - progress);
      const easedProgress = easeOutQuad(progress);
      
      // Berechne neue Breite
      const newWidth = currentWidth + (targetPercent - currentWidth) * easedProgress;
      progressFill.style.width = `${newWidth}%`;
      
      // Weiter animieren, wenn wir noch nicht fertig sind
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        console.log("Animation abgeschlossen:", progressFill.style.width);
      }
    };
    
    requestAnimationFrame(animate);
  }
  
  // Markiere erreichte Stufen
  updateProgressMarkers(progressBar, cartValue, threshold1, threshold2, threshold3) {
    // Stufe 1
    const step1 = progressBar.querySelector('.step-1');
    if (step1) {
      if (cartValue >= threshold1 / 100) {
        step1.classList.add('achieved');
      } else {
        step1.classList.remove('achieved');
      }
    }
    
    // Stufe 2
    const step2 = progressBar.querySelector('.step-2');
    if (step2 && threshold2 > 0) {
      if (cartValue >= threshold2 / 100) {
        step2.classList.add('achieved');
      } else {
        step2.classList.remove('achieved');
      }
    }
    
    // Stufe 3
    const step3 = progressBar.querySelector('.step-3');
    if (step3 && threshold3 > 0) {
      if (cartValue >= threshold3 / 100) {
        step3.classList.add('achieved');
      } else {
        step3.classList.remove('achieved');
      }
    }
  }
  
  // Aktualisiere die Hinweistexte
  updateProgressMessage(progressBar, cartValue, threshold1, threshold2, threshold3) {
    const messageElement = progressBar.querySelector('.progress-current-message');
    if (!messageElement) return;
    
    // Hole die Nachrichtentexte aus den Theme-Settings
    const notStartedTemplate = window.theme?.progressMessages?.notStarted || '{{amount}} € bis {{next_reward}}';
    const partialTemplate = window.theme?.progressMessages?.partial || 'Noch {{amount}} € bis {{next_reward}}';
    const completeTemplate = window.theme?.progressMessages?.complete || 'Alle Vorteile freigeschaltet!';
    
    // Konvertiere Threshold-Werte (von Cents zu Euro)
    const t1 = threshold1 / 100;
    const t2 = threshold2 / 100;
    const t3 = threshold3 / 100;
    
    // Finde alle aktiven Schwellenwerte (größer als 0)
    const activeThresholds = [];
    const activeRewards = [];
    
    if (t1 > 0) {
      activeThresholds.push(t1);
      activeRewards.push(progressBar.querySelector('.reward-1')?.textContent.trim() || '');
    }
    
    if (t2 > 0) {
      activeThresholds.push(t2);
      activeRewards.push(progressBar.querySelector('.reward-2')?.textContent.trim() || '');
    }
    
    if (t3 > 0) {
      activeThresholds.push(t3);
      activeRewards.push(progressBar.querySelector('.reward-3')?.textContent.trim() || '');
    }
    
    // Sortiere die Schwellenwerte aufsteigend
    // und die Rewards entsprechend
    const sortedPairs = activeThresholds.map((threshold, index) => {
      return { threshold, reward: activeRewards[index] };
    }).sort((a, b) => a.threshold - b.threshold);
    
    // Bestimme den nächsten zu erreichenden Meilenstein
    let message = '';
    
    // Wenn der Warenkorb-Wert größer oder gleich dem höchsten Schwellenwert ist
    if (activeThresholds.length > 0 && cartValue >= Math.max(...activeThresholds)) {
      message = completeTemplate;
    } else {
      // Finde den nächsten Schwellenwert, der noch nicht erreicht wurde
      const nextThresholdPair = sortedPairs.find(pair => cartValue < pair.threshold);
      
      if (nextThresholdPair) {
        // Nächster Schwellenwert gefunden
        const amountLeft = (nextThresholdPair.threshold - cartValue).toFixed(2).replace('.', ',');
        
        if (cartValue > 0) {
          // Teilweise Fortschritt
          message = partialTemplate
            .replace('{{amount}}', amountLeft)
            .replace('{{next_reward}}', nextThresholdPair.reward);
        } else {
          // Noch kein Fortschritt
          message = notStartedTemplate
            .replace('{{amount}}', amountLeft)
            .replace('{{next_reward}}', nextThresholdPair.reward);
        }
      } else {
        // Dieser Fall sollte eigentlich nicht auftreten
        message = 'Füge Produkte zu deinem Warenkorb hinzu';
      }
    }
    
    messageElement.textContent = message;
  }
  
  // Zeige die Notification an
  showNotification() {
    return
    const notification = document.querySelector('.progress-bar-notification');
    if (!notification) {
      console.log("Keine Notification gefunden");
      return;
    }
    
    // Notification anzeigen
    notification.classList.add('active');
    console.log("Notification angezeigt");
    
    // Timer zurücksetzen und neu starten
    if (this.notificationTimer) {
      clearTimeout(this.notificationTimer);
    }
    
    this.notificationTimer = setTimeout(() => {
      notification.classList.remove('active');
      console.log("Notification ausgeblendet");
    }, this.notificationDuration);
  }
}

// Initialisiere die Progress Bar
document.addEventListener('DOMContentLoaded', () => {
  console.log("DOM geladen, Progress Bar initialisieren");
  window.shopifyProgressBar = new ShopifyProgressBar();
});

// Für Themes, die AJAX für das Laden der Seite verwenden
// Bereits geladen? Dann sofort initialisieren
if (document.readyState === 'complete') {
  console.log("Seite bereits geladen, Progress Bar initialisieren");
  if (!window.shopifyProgressBar) {
    window.shopifyProgressBar = new ShopifyProgressBar();
  }
}
